import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { SectionTitle } from "@/components/common";
import { toast } from "sonner";
import { Loader2, MonitorSmartphone, LogOut, ShieldOff, PowerOff } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" }); }
  catch { return iso; }
};
const device = (ua = "") => {
  if (!ua) return "Dispositivo sconosciuto";
  const os = /Windows/i.test(ua) ? "Windows" : /Mac/i.test(ua) ? "macOS" : /Android/i.test(ua) ? "Android" : /iPhone|iPad|iOS/i.test(ua) ? "iOS" : /Linux/i.test(ua) ? "Linux" : "";
  const br = /Edg/i.test(ua) ? "Edge" : /Chrome/i.test(ua) ? "Chrome" : /Firefox/i.test(ua) ? "Firefox" : /Safari/i.test(ua) ? "Safari" : "Browser";
  return [br, os].filter(Boolean).join(" · ") || "Browser";
};
const ROLE_LABEL = { direzione: "Direzione", direttore_commerciale: "Direttore", agente: "Agente" };

export default function Sessioni() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [confirmUser, setConfirmUser] = useState(null); // { user_id, user_name, user_email, count }

  const load = () => {
    setLoading(true);
    api.get("/sessions").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Group sessions by user
  const groups = useMemo(() => {
    const map = new Map();
    for (const s of items) {
      const key = s.user_id || s.user_email;
      if (!map.has(key)) {
        map.set(key, {
          user_id: s.user_id, user_name: s.user_name, user_email: s.user_email,
          user_role: s.user_role, hasCurrent: false, sessions: [],
        });
      }
      const g = map.get(key);
      g.sessions.push(s);
      if (s.current) g.hasCurrent = true;
    }
    return Array.from(map.values());
  }, [items]);

  const revoke = async (id) => {
    setBusy(id);
    try { await api.post(`/sessions/${id}/revoke`); toast.success("Sessione revocata."); load(); }
    catch { toast.error("Errore nella revoca."); } finally { setBusy(""); }
  };
  const revokeOthers = async () => {
    setBusy("others");
    try { const r = await api.post("/sessions/revoke-others"); toast.success(`${r.data.revoked} sessioni terminate.`); load(); }
    catch { toast.error("Errore."); } finally { setBusy(""); }
  };
  const revokeAllUser = async () => {
    if (!confirmUser) return;
    const uid = confirmUser.user_id;
    setBusy(`user-${uid}`);
    try {
      const r = await api.post(`/sessions/user/${uid}/revoke-all`);
      toast.success(`${r.data.revoked} sessioni terminate per ${confirmUser.user_name}.`);
      setConfirmUser(null); load();
    } catch { toast.error("Errore nella terminazione delle sessioni."); }
    finally { setBusy(""); }
  };

  return (
    <div className="fade-up" data-testid="sessioni-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <SectionTitle sub="Tutte le sessioni attive degli utenti, raggruppate per persona. Revoca l'accesso da remoto in caso di dispositivo smarrito o sospetto.">Sessioni attive</SectionTitle>
        <button data-testid="revoke-others-btn" onClick={revokeOthers} disabled={busy === "others"}
                className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium transition-colors disabled:opacity-60">
          {busy === "others" ? <Loader2 size={15} className="animate-spin" /> : <ShieldOff size={15} />} Termina tutte tranne questa
        </button>
      </div>

      {loading ? (
        <div className="mt-8 flex justify-center"><Loader2 className="animate-spin text-[#FC82ED]" /></div>
      ) : groups.length === 0 ? (
        <div className="mt-8 text-sm text-[var(--muted)]">Nessuna sessione attiva.</div>
      ) : (
        <div className="mt-6 space-y-6">
          {groups.map((g) => (
            <div key={g.user_id || g.user_email} data-testid={`user-group-${g.user_id}`} className="card-immo p-4">
              <div className="flex items-center justify-between gap-4 flex-wrap pb-3 mb-3 border-b border-[var(--border)]">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-[#FC82ED]/12 border border-[#FC82ED]/25 flex items-center justify-center text-[#FC82ED] font-bold shrink-0">
                    {(g.user_name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm truncate">{g.user_name}</span>
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-[#FC82ED]/10 text-[#FC82ED] font-medium">{ROLE_LABEL[g.user_role] || g.user_role}</span>
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-[var(--muted)] font-medium">{g.sessions.length} sessioni</span>
                    </div>
                    <div className="text-xs text-[var(--muted)] truncate">{g.user_email}</div>
                  </div>
                </div>
                <button
                  data-testid={`revoke-all-user-${g.user_id}`}
                  onClick={() => setConfirmUser({ user_id: g.user_id, user_name: g.user_name, user_email: g.user_email, count: g.sessions.length })}
                  disabled={busy === `user-${g.user_id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-xs font-medium transition-colors disabled:opacity-60">
                  {busy === `user-${g.user_id}` ? <Loader2 size={13} className="animate-spin" /> : <PowerOff size={13} />} Termina tutte le sessioni
                </button>
              </div>

              <div className="grid gap-2">
                {g.sessions.map((s) => (
                  <div key={s.id} data-testid={`session-${s.id}`}
                       className={`rounded-lg p-3 flex items-center gap-3 border ${s.current ? "border-[#FC82ED]/50 bg-[#FC82ED]/[0.04]" : "border-[var(--border)]"}`}>
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-[var(--muted)] shrink-0"><MonitorSmartphone size={16} /></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium">{device(s.user_agent)}</span>
                        {s.current && <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-400/15 text-emerald-600 font-medium">Questa sessione</span>}
                      </div>
                      <div className="text-xs text-[var(--muted)] mt-0.5">IP {s.ip || "—"} · attiva dal {fmt(s.created_at)} · vista {fmt(s.last_seen)}</div>
                    </div>
                    {!s.current && (
                      <button data-testid={`revoke-${s.id}`} onClick={() => revoke(s.id)} disabled={busy === s.id}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-[var(--muted)] hover:text-red-600 hover:border-red-200 text-xs font-medium transition-colors disabled:opacity-60">
                        {busy === s.id ? <Loader2 size={13} className="animate-spin" /> : <LogOut size={13} />} Revoca
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!confirmUser} onOpenChange={(o) => { if (!o) setConfirmUser(null); }}>
        <AlertDialogContent data-testid="revoke-all-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Terminare tutte le sessioni?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per terminare tutte le <strong>{confirmUser?.count}</strong> sessioni attive di{" "}
              <strong>{confirmUser?.user_name}</strong> ({confirmUser?.user_email}). L'utente dovrà effettuare di nuovo il login su tutti i dispositivi. L'azione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="revoke-all-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="revoke-all-confirm-btn" onClick={revokeAllUser}
                               className="bg-red-600 hover:bg-red-700 text-white">
              Termina tutte
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
